import { useMemo, useState } from 'react';
import { BaseButton } from '@repo/common';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';
import PathfinderMobileQuestionLayout from './PathfinderMobileQuestionLayout';
import PathfinderMobileSelect from './PathfinderMobileSelect';

const TIMELINE_DATE_OPTIONS = [
  { label: 'Within 3 months', value: 'WITHIN_3M' },
  { label: 'Within 6 months', value: 'WITHIN_6M' },
  { label: 'Within 1 year', value: 'WITHIN_1Y' },
  { label: 'More than 1 year', value: 'OVER_1Y' },
];

const resolveTimelineOptionTitle = (option) =>
  option.optionCode === 'TIMELINE_NOT_DECIDED' ? 'None' : option.optionLabel;

const normalizeSelections = (value) => {
  if (Array.isArray(value?.selections)) return value.selections;
  if (Array.isArray(value?.step1)) return value.step1;
  if (Array.isArray(value?.step2)) return value.step2;
  return [];
};

const normalizeDates = (value) => value?.dates || value?.step2Durations || {};

function TimelineDateCard({
  option,
  value,
  open,
  onToggle,
  onChange,
}) {
  const selectedLabel = TIMELINE_DATE_OPTIONS.find(
    (dateOption) => dateOption.value === value
  )?.label;
  const ArrowIcon = open ? ArrowBottomIcon : ArrowTopIcon;

  return (
    <div className='flex w-full flex-col overflow-hidden rounded-[12px] border-[1.6px] border-grayscale-gray01 bg-grayscale-gray00'>
      <BaseButton
        type='text'
        size='m'
        htmlType='button'
        typo={false}
        isStatic
        leftalign
        gap='gap-2'
        iconPosition='after'
        icon={
          <ArrowIcon className='size-4 shrink-0 text-text-tertiary' aria-hidden />
        }
        onClick={onToggle}
        textWrapperClass='h-auto min-w-0 flex-1 truncate text-left typo-b16Strong text-text-primary'
        className='h-12 w-full min-w-0 cursor-pointer items-center gap-2 rounded-none border-0 border-b-[1.6px] border-grayscale-gray01 bg-transparent py-[11px] pl-3 pr-4 text-left'
      >
        {selectedLabel ? `${option.title} · ${selectedLabel}` : option.title}
      </BaseButton>

      {open ? (
        <div className='flex flex-wrap gap-2 p-3'>
          {TIMELINE_DATE_OPTIONS.map((dateOption) => (
            <PathfinderMobileSelect
              key={dateOption.value}
              size='chip'
              title={dateOption.label}
              state={value === dateOption.value ? 'selected' : 'default'}
              onClick={() => onChange(dateOption.value)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PathfinderMobileTimelineFlow({
  value,
  onChange,
  onComplete,
  onBack,
  config = {},
  options = [],
  progressLabel,
  questionTitle = 'Which timeline do you already know?',
  questionHelperText = 'A more tailored Pathfinder will be created based on your target timeline.',
  loading = false,
}) {
  const {
    step2Title = 'When do you plan to start each program?',
    step2Helper = 'Set an expected start date for each selection',
    step1Options: step1Codes = [],
    noDateOptions = [],
  } = config;

  const timelineOptions = useMemo(
    () =>
      options
        .filter((option) => step1Codes.includes(option.optionCode))
        .sort(
          (left, right) =>
            step1Codes.indexOf(left.optionCode) - step1Codes.indexOf(right.optionCode)
        )
        .map((option) => ({
          id: option.optionCode,
          title: resolveTimelineOptionTitle(option),
        })),
    [options, step1Codes]
  );

  const initialSelections = normalizeSelections(value);
  const initialDates = normalizeDates(value);
  const initialNeedsDateSelections = initialSelections.filter(
    (code) => !noDateOptions.includes(code)
  );

  const [subStep, setSubStep] = useState(() =>
    initialSelections.length > 0 && initialNeedsDateSelections.length > 0 ? 2 : 1
  );
  const [selections, setSelections] = useState(initialSelections);
  const [dates, setDates] = useState(initialDates);
  const [openCode, setOpenCode] = useState(() => {
    const firstIncomplete = initialNeedsDateSelections.find((code) => !initialDates[code]);
    return firstIncomplete ?? initialNeedsDateSelections[0] ?? null;
  });

  const timelineOptionMap = useMemo(
    () => new Map(timelineOptions.map((option) => [option.id, option])),
    [timelineOptions]
  );
  const needsDateSelections = selections.filter(
    (code) => !noDateOptions.includes(code)
  );
  const allDatesComplete =
    selections.length > 0 &&
    selections.every((code) => noDateOptions.includes(code) || Boolean(dates[code]));

  const buildValue = (
    nextSelections = selections,
    nextDates = dates,
    complete = false
  ) => ({
    selections: nextSelections,
    dates: Object.fromEntries(
      Object.entries(nextDates).filter(([code]) => nextSelections.includes(code))
    ),
    complete,
    experiences: [],
  });

  const toggleSelection = (optionId) => {
    const isSelected = selections.includes(optionId);
    const nextSelections = isSelected
      ? selections.filter((code) => code !== optionId)
      : [...selections, optionId];
    const nextDates = Object.fromEntries(
      Object.entries(dates).filter(([code]) => nextSelections.includes(code))
    );

    setSelections(nextSelections);
    setDates(nextDates);
    onChange?.(buildValue(nextSelections, nextDates, false));
  };

  const handleDateChange = (code, dateValue) => {
    const nextDates = { ...dates, [code]: dateValue };
    setDates(nextDates);
    setOpenCode(null);
    onChange?.(buildValue(selections, nextDates, false));
  };

  const handleBack = () => {
    if (subStep === 1) {
      onBack?.();
      return;
    }
    setSubStep(1);
  };

  const handleNext = () => {
    if (subStep === 1) {
      if (selections.length === 0) return;
      if (needsDateSelections.length === 0) {
        onComplete?.(buildValue(selections, dates, true));
        return;
      }
      setOpenCode(
        needsDateSelections.find((code) => !dates[code]) ?? needsDateSelections[0]
      );
      setSubStep(2);
      return;
    }

    if (allDatesComplete) {
      onComplete?.(buildValue(selections, dates, true));
    }
  };

  const nextDisabled =
    loading || (subStep === 1 ? selections.length === 0 : !allDatesComplete);

  return (
    <PathfinderMobileQuestionLayout
      progressLabel={progressLabel}
      title={subStep === 1 ? questionTitle : step2Title}
      helperText={subStep === 1 ? questionHelperText : step2Helper}
      onBack={handleBack}
      onNext={handleNext}
      nextDisabled={nextDisabled}
    >
      {subStep === 1 ? (
        <div className='flex flex-col gap-3'>
          {timelineOptions.map((option) => (
            <PathfinderMobileSelect
              key={option.id}
              icon={null}
              iconPosition='none'
              textAlign='left'
              title={option.title}
              caption=''
              state={selections.includes(option.id) ? 'selected' : 'default'}
              onClick={() => toggleSelection(option.id)}
            />
          ))}
        </div>
      ) : null}

      {subStep === 2 ? (
        <div className='flex flex-col gap-8'>
          {needsDateSelections.map((code) => {
            const option = timelineOptionMap.get(code);
            if (!option) return null;

            return (
              <TimelineDateCard
                key={code}
                option={option}
                value={dates[code]}
                open={openCode === code}
                onToggle={() =>
                  setOpenCode((current) => (current === code ? null : code))
                }
                onChange={(dateValue) => handleDateChange(code, dateValue)}
              />
            );
          })}
        </div>
      ) : null}
    </PathfinderMobileQuestionLayout>
  );
}

export default PathfinderMobileTimelineFlow;
